// Phase I3 — Player DNA panel.
// Maps super_acca_player_performance → key-performer rows. When no
// players have been observed we render the operator-facing "DNA
// accumulating" copy with the live observed_players sample.

import { Eyebrow } from "../../../components/super-acca";
import { CATEGORY } from "../../../super-acca/categories";
import type { PlayerPerformanceView } from "../../../super-acca/view-models";

export interface PlayerDNAPanelProps {
  data: PlayerPerformanceView | null;
}

export function PlayerDNAPanel({ data }: PlayerDNAPanelProps) {
  if (!data || !data.sample_valid) {
    const observed = data?.observed_players ?? 0;
    return (
      <div
        style={{
          padding: 22,
          background: "var(--sa-bg-panel)",
          border: "1px solid var(--sa-hairline)",
          borderRadius: 4,
        }}
      >
        <Eyebrow color={CATEGORY.PLAYER.color}>Player DNA · Key Performers</Eyebrow>
        <p
          style={{
            fontFamily: "var(--sa-font-body)",
            fontSize: 13.5,
            color: "var(--sa-fg-secondary)",
            marginTop: 12,
            lineHeight: 1.6,
          }}
        >
          Player DNA accumulating — {observed} players observed across this
          fixture. The Player Performance agent activates per-player once 10
          settled matches are on file.
        </p>
      </div>
    );
  }
  return (
    <div
      style={{
        padding: 22,
        background: "var(--sa-bg-panel)",
        border: "1px solid var(--sa-hairline)",
        borderRadius: 4,
      }}
    >
      <Eyebrow color={CATEGORY.PLAYER.color}>
        Player DNA · {data.observed_players} performers
      </Eyebrow>
      <div style={{ marginTop: 14 }}>
        {data.all_players.map((p, i) => (
          <div
            key={`${p.name}|${p.team}`}
            style={{
              display: "grid",
              gridTemplateColumns: "1.5fr 70px 70px 70px 100px",
              gap: 14,
              padding: "14px 0",
              alignItems: "center",
              borderTop: i === 0 ? "none" : "1px solid var(--sa-hairline)",
              fontFamily: "var(--sa-font-mono)",
              fontSize: 12,
            }}
          >
            <div>
              <div
                style={{
                  color: "var(--sa-fg-primary)",
                  fontFamily: "var(--sa-font-body)",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                {p.name}{" "}
                <span style={{ color: "var(--sa-fg-muted)", fontSize: 11 }}>· {p.team}</span>
              </div>
              {p.streak ? (
                <div
                  style={{
                    color: "var(--sa-fg-muted)",
                    fontSize: 11,
                    marginTop: 2,
                  }}
                >
                  {p.streak}
                </div>
              ) : null}
            </div>
            <div style={{ color: "var(--sa-fg-primary)" }}>
              SH/90{" "}
              <span style={{ color: "var(--sa-accent-saffron)" }}>{p.shots90 ?? "—"}</span>
            </div>
            <div style={{ color: "var(--sa-fg-primary)" }}>
              SOT/90{" "}
              <span style={{ color: "var(--sa-accent-saffron)" }}>{p.sot90 ?? "—"}</span>
            </div>
            <div style={{ color: "var(--sa-accent-saffron)" }}>
              A·G {p.ag ?? "—"}
            </div>
            <div
              style={{
                color: p.risk_color === "warn" ? "var(--sa-signal-warn)" : "var(--sa-signal-green)",
                textAlign: "right",
              }}
            >
              {p.risk ?? "OK"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
